import { DragControls } from "three/examples/jsm/controls/DragControls.js";
import { Line2 } from "three/examples/jsm/lines/Line2.js";
import { LineMaterial } from "three/examples/jsm/lines/LineMaterial.js";
import { CSS2DObject } from "three/examples/jsm/renderers/CSS2DRenderer.js";
import { LoadCaseType, LoadType, MassType, deriveMassType, normalizeInertiaTensor, normalizeReferencePoint } from "./enums";
import { normalizeCoordinateSystemId } from "../utils/local-frame.util";
import { AccelerationItem, ExcitedSupportItem, FrequencyTableEntry } from "./interfaces";
import { SessionService } from "../services/session.service";
import { ArrowHelper } from '../engine/addons/load-helpers/arrowhelper';
import { Object3D } from "three";

export class LoadItem {

    constructor(
        public id: string,
        public partId: any,
        public partFaces: string[] = [],
        public type: LoadType = LoadType.FORCE,
        // FORCE
        public remotePoint: number[] = [0, 0, 0], // XYZ
        public forceComponent: number[] = [0, -1, 0], // XYZ
        public momentComponent: number[] = [0, 0, 0], // XYZ
        // PRESSURE
        public pressureMagnitude: number = 0.1,
        // ACCELERATION
        public nonStructuralMass: number = 0,
        public centerOfMass: number[] = [0, 0, 0],
        public checked = true,
        public massType: MassType = 'nonStructural',
        public inertiaTensor: number[] = [0, 0, 0, 0, 0, 0], // [I11, I22, I33, I23, I13, I12] kg·mm²
        public referencePoint: number[] | null = null, // RemoteMass pivot; null = follow remotePoint
        public coordinateSystemId: string | null = null // null = global frame
    ) {

    }

    static fromJSON(data: any) {
        const item = new LoadItem(
            data.id,
            data.partId,
            data.partFaces,
            data.type,
            data.remotePoint,
            data.forceComponent,
            data.momentComponent,
            data.pressureMagnitude,
            data.nonStructuralMass,
            data.centerOfMass
        );
        item.massType = data.massType === 'remotePoint' && data.remotePoint
            ? 'remotePoint'
            : data.massType === 'nonStructural'
                ? 'nonStructural'
                : deriveMassType(data.remotePoint, data.centerOfMass);
        item.inertiaTensor = normalizeInertiaTensor(data.inertiaTensor);
        item.referencePoint = normalizeReferencePoint(data.referencePoint);
        item.coordinateSystemId = normalizeCoordinateSystemId(data.coordinateSystemId);
        return item;
    }

    toJSON() {
        return {
            id: this.id,
            partId: this.partId,
            partFaces: this.partFaces,
            type: this.type,
            // FORCE
            remotePoint: this.remotePoint, // XYZ
            forceComponent: this.forceComponent, // XYZ
            momentComponent: this.momentComponent, // XYZ
            // PRESSURE
            pressureMagnitude: this.pressureMagnitude,
            // ACCELERATION
            nonStructuralMass: this.nonStructuralMass,
            centerOfMass: this.centerOfMass,
            massType: this.massType,
            inertiaTensor: this.inertiaTensor,
            referencePoint: this.referencePoint,
            coordinateSystemId: this.coordinateSystemId
        }
    }

    static parse() { }

    clone() { }

    getPartName(session: SessionService) {
        const item = session.engine?.objects.find(obj => obj.data.id === this.partId);
        if (item) {
            return item.data.name;
        }
        return '';
    }

}

export class BoundaryConditionItem {

    constructor(
        public id: string,
        public partId: any,
        public partFaces: string[] = [],
        public fixedX: boolean = true,
        public fixedY: boolean = true,
        public fixedZ: boolean = true,
        public rotationAlongAxis: boolean = false,
        public rotationAxisX: number = 0,
        public rotationAxisY: number = 0,
        public rotationAxisZ: number = 0,
        public coordinateSystemId: string | null = null // null = global frame
    ) {

    }

    static fromJSON(data: any, newId?: string) {
        if (newId) {
            return new BoundaryConditionItem(
                newId,
                data.partId,
                data.partFaces,
                data.fixedX,
                data.fixedY,
                data.fixedZ,
                data.rotationAlongAxis,
                data.rotationAxisX,
                data.rotationAxisY,
                data.rotationAxisZ,
                normalizeCoordinateSystemId(data.coordinateSystemId)
            );
        }
        return new BoundaryConditionItem(
            data.id,
            data.partId,
            data.partFaces,
            data.fixedX,
            data.fixedY,
            data.fixedZ,
            data.rotationAlongAxis,
            data.rotationAxisX,
            data.rotationAxisY,
            data.rotationAxisZ,
            normalizeCoordinateSystemId(data.coordinateSystemId)
        );
    }

    toJSON() {
        return {
            id: this.id,
            partId: this.partId,
            partFaces: this.partFaces,
            fixedX: this.fixedX,
            fixedY: this.fixedY,
            fixedZ: this.fixedZ,
            rotationAlongAxis: this.rotationAlongAxis,
            rotationAxisX: this.rotationAxisX,
            rotationAxisY: this.rotationAxisY,
            rotationAxisZ: this.rotationAxisZ,
            coordinateSystemId: this.coordinateSystemId
        };
    }

    static parse() { }

    clone() { }

    getPartName(session: SessionService) {
        const item = session.engine?.objects.find(obj => obj.data.id === this.partId);
        if (item) {
            return item.data.name;
        }
        return '';
    }

}

export interface GroupItemPart {
    id: string;
    faces: string[];
}

export class LoadsGroupItem {

    public groupEditStack: {
        force?: ArrowHelper,
        moment?: ArrowHelper,
        controlMass: { sphere?: Object3D, drag?: DragControls, annotation?: CSS2DObject },
        resources: { lines?: Line2[], materials?: LineMaterial[] }
    } | undefined = undefined;

    constructor(
        public id: string,
        public parts: GroupItemPart[] = [],
        public type: LoadType = LoadType.FORCE,
        // FORCE
        public remotePoint: number[] = [0, 0, 0], // XYZ
        public centerOfMass: number[] = [0, 0, 0], // XYZ
        public forceComponent: number[] = [0, -1, 0], // XYZ
        public momentComponent: number[] = [0, 0, 0], // XYZ
        // PRESSURE
        public pressureMagnitude: number = 0.1,
        // ACCELERATION
        public nonStructuralMass: number = 0,
        public massType: MassType = 'nonStructural',
        public inertiaTensor: number[] = [0, 0, 0, 0, 0, 0], // [I11, I22, I33, I23, I13, I12] kg·mm²
        public referencePoint: number[] | null = null, // RemoteMass pivot; null = follow remotePoint
        public coordinateSystemId: string | null = null // null = global frame
    ) {

    }

    static fromJSON(data: any) {
        const item = new LoadsGroupItem(
            data.id,
            data.parts,
            data.type,
            data.remotePoint,
            data.centerOfMass,
            data.forceComponent,
            data.momentComponent,
            data.pressureMagnitude,
            data.nonStructuralMass
        );
        item.massType = data.massType === 'remotePoint' && data.remotePoint
            ? 'remotePoint'
            : data.massType === 'nonStructural'
                ? 'nonStructural'
                : deriveMassType(data.remotePoint, data.centerOfMass);
        item.inertiaTensor = normalizeInertiaTensor(data.inertiaTensor);
        item.referencePoint = normalizeReferencePoint(data.referencePoint);
        item.coordinateSystemId = normalizeCoordinateSystemId(data.coordinateSystemId);
        return item;
    }

    toJSON() {
        return {
            id: this.id,
            parts: this.parts,
            type: this.type,
            // FORCE
            centerOfMass: this.centerOfMass,
            remotePoint: this.remotePoint, // XYZ
            forceComponent: this.forceComponent, // XYZ
            momentComponent: this.momentComponent, // XYZ
            // PRESSURE
            pressureMagnitude: this.pressureMagnitude,
            // ACCELERATION
            nonStructuralMass: this.nonStructuralMass,
            massType: this.massType,
            inertiaTensor: this.inertiaTensor,
            referencePoint: this.referencePoint,
            coordinateSystemId: this.coordinateSystemId
        }
    }

    static parse() { }

    clone() { }

    list(session: SessionService) {
        const parts: { name: string, partId: string }[] = [];
        this.parts.forEach(part => {
            const item = session.engine?.objects.find(obj => obj.data.id === part.id);
            if (item) {
                parts.push({
                    name: item.data.name,
                    partId: item.data.id
                });
            }
        });
        return parts;
    }

}


export class LoadCaseItem {

    constructor(
        public id: string,
        public name?: string,
        public type: LoadCaseType = LoadCaseType.STATIC,
        public weightingFactor: number = 1,
        public boundaryConditions: BoundaryConditionItem[] = [],
        public loads: LoadItem[] = [],
        public acceleration: AccelerationItem = { accelerationDirectionVector: [0, 0, -1], accelerationGravity: 1 },
        public stressConstraint: number = 100,
        public minimumDeformationMagnitude: number = 0,
        public maximumDeformationMagnitude: number = 0,
        public deformationPoints: { x: number, y: number, z: number, c: number }[] = [],
        public deformationGlobal = true,
        public groups: LoadsGroupItem[] = [],
        public damping: number = 2,
        public frequencyTable: FrequencyTableEntry[] = [],
        public plotType: 'Log' | 'Linear' = 'Log',
        public baseParts: string[] = [],
        public samplingType: 'Even' | 'EigenValue' = 'Even',
        public baseFreqNumber: number = 20,
        public excitedSupports: ExcitedSupportItem[] = []
    ) {

    }

    static parse() { }

    clone() { }

    static fromJSON(data: any) {
        const loadcaseItem = new LoadCaseItem(
            data.id,
            data.name,
            data.type,
            data.weightingFactor,
            [],
            [],
            data.acceleration,
            data.stressConstraint,
            data.minimumDeformationMagnitude,
            data.maximumDeformationMagnitude,
            data.deformationPoints,
            data.deformationGlobal,
            [],
            data.damping ?? 2,
            data.frequencyTable ?? data.psdTable ?? [],
            data.plotType ?? 'Log',
            data.baseParts ?? [],
            data.samplingType ?? 'Even',
            data.baseFreqNumber ?? 20,
            data.excitedSupports ?? []
        );
        if (!data.boundaryConditions) data.boundaryConditions = [];
        if (!data.loads) data.loads = [];
        if (!data.groups) data.groups = [];
        loadcaseItem.boundaryConditions = data.boundaryConditions.map((item: any) => BoundaryConditionItem.fromJSON(item));
        loadcaseItem.loads = data.loads.map((item: any) => LoadItem.fromJSON(item));
        loadcaseItem.groups = data.groups.map((item: any) => LoadsGroupItem.fromJSON(item));
        return loadcaseItem;
    }

    toJSON() {
        return JSON.parse(
            JSON.stringify({
                id: this.id,
                name: this.name,
                type: this.type,
                boundaryConditions: this.boundaryConditions.map(item => item.toJSON()),
                loads: this.loads.map(item => item.toJSON()),
                weightingFactor: this.weightingFactor,
                acceleration: this.acceleration,
                stressConstraint: this.stressConstraint,
                minimumDeformationMagnitude: this.minimumDeformationMagnitude,
                maximumDeformationMagnitude: this.maximumDeformationMagnitude,
                deformationPoints: this.deformationPoints,
                deformationGlobal: this.deformationGlobal,
                groups: this.groups.map(item => item.toJSON()),
                damping: this.damping,
                frequencyTable: this.frequencyTable,
                plotType: this.plotType,
                baseParts: this.baseParts,
                samplingType: this.samplingType,
                baseFreqNumber: this.baseFreqNumber,
                excitedSupports: this.excitedSupports
            })
        );
    }

}